import type { DropDownResult } from '@myosh/odin-components';
import { DynamicFormRequiredType, ErrorLabel, IconButton, Input, OdinIconSize } from '@myosh/odin-components';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { forceAssert } from '../../../utils/type-guards';
import {
  nonNegativeIntPattern,
  positiveIntMinOnePattern,
  positiveIntOneToThirtyPattern,
  positiveIntOneToTwentyEightPattern,
  positiveIntZeroToTwentyThreePattern,
} from '../../../utils/validation-utils';
import OdinDropDown from '../custom-drop-downs/odin-drop-down.component';
import type { IntervalType } from './scheduler-rules-utils';
import { dayValues, getAllTimezones, monthValues, timeIntervals } from './scheduler-rules-utils';

export interface IntervalFieldRuleState {
  scheduledRecordCreationType?: IntervalType;
  interval?: number;
  month?: IntervalType;
  dayOfMonth?: number;
  dayOfWeek?: IntervalType;
  timeZone?: DropDownResult;
  hourOfDay?: number;
  id?: number | string;
  type?: string;
}

export type IntervalFieldRuleProps = {
  intervalFieldRule?: IntervalFieldRuleState;
  className?: string;
  errorObject?: Record<string, unknown>;
  onChange?: (value: IntervalFieldRuleState, type: string) => void;
  onFieldDelete?: (value: IntervalFieldRuleState) => void;
};

const timezones = getAllTimezones();
const IntervalFieldRule = ({
  intervalFieldRule,
  className,
  errorObject,
  onChange,
  onFieldDelete,
}: IntervalFieldRuleProps) => {
  const { t } = useTranslation();

  const [formState, setFormState] = useState<IntervalFieldRuleState>({});

  useEffect(() => {
    if (intervalFieldRule) {
      setFormState(intervalFieldRule);
      if (String(intervalFieldRule?.id).includes('new')) {
        onChange?.(intervalFieldRule, 'periodic-schedule');
      }
    }
  }, []);

  const handleDropDownChange = (name: keyof typeof formState) => (result: DropDownResult) => {
    const updatedState = {
      ...formState,
      [name]: result && 'value' in result ? result.originalData : undefined,
    };
    onChange?.(updatedState, 'periodic-schedule');
    setFormState(updatedState);
  };

  const handleInputChange = (name: keyof typeof formState) => (value?: string | number) => {
    const updatedState = {
      ...formState,
      [name]: value,
    };
    onChange?.(updatedState, 'periodic-schedule');
    setFormState(updatedState);
  };

  const onDeleteHandler = () => {
    onFieldDelete?.(formState);
  };

  const { interval, scheduledRecordCreationType, dayOfWeek, month, dayOfMonth, timeZone, hourOfDay } = formState;

  const ruleCardStyles = clsx('flex-col', className, {
    'odin-border-gray-4': !errorObject,
    'odin-border-error': errorObject,
  });

  return (
    <>
      <div className={ruleCardStyles}>
        <div className="mt-2 flex w-full flex-row space-x-4">
          <div className="mt-0.5 flex h-12 w-full flex-row items-end gap-4">
            <Input
              label={t('every')}
              name="interval"
              type="number"
              value={interval?.toString() ?? ''}
              readOnly={false}
              validations={[
                { pattern: positiveIntMinOnePattern, errorLabel: `${t('min-value')}: 1` },
                { pattern: positiveIntOneToThirtyPattern, errorLabel: `${t('max-value')}: 30` },
              ]}
              error={forceAssert<string>(Boolean(errorObject?.interval))}
              required={DynamicFormRequiredType.True}
              onChange={handleInputChange('interval')}
            />

            <OdinDropDown
              value={scheduledRecordCreationType}
              data={timeIntervals}
              valueField="value"
              textField="text"
              readOnly={false}
              error={forceAssert<string>(Boolean(errorObject?.scheduledRecordCreationType))}
              required={DynamicFormRequiredType.True}
              onChange={(value) => handleDropDownChange('scheduledRecordCreationType')(value as DropDownResult)}
            />
          </div>
          <IconButton
            icon="Trash"
            iconSize={OdinIconSize.Small}
            classNames="self-end justify-self-end pl-2 ml-auto"
            onClick={onDeleteHandler}
          />
        </div>
        <div className="mt-2 flex flex-row space-x-4">
          {scheduledRecordCreationType?.displayWeek === true && (
            <OdinDropDown
              label={t('day-of-week')}
              value={dayOfWeek}
              className="self-end"
              data={dayValues}
              valueField="value"
              textField="text"
              readOnly={false}
              error={forceAssert<string>(Boolean(errorObject?.dayOfWeek))}
              required={DynamicFormRequiredType.True}
              onChange={(value) => handleDropDownChange('dayOfWeek')(value as DropDownResult)}
            />
          )}
          {scheduledRecordCreationType?.displayMonth === true && (
            <OdinDropDown
              value={month}
              data={monthValues}
              className="self-end"
              label={t('month')}
              valueField="value"
              textField="text"
              readOnly={false}
              error={forceAssert<string>(Boolean(errorObject?.month))}
              required={DynamicFormRequiredType.True}
              onChange={(value) => handleDropDownChange('month')(value as DropDownResult)}
            />
          )}
          {/** Day number */}
          {scheduledRecordCreationType?.displayDayOfMonth === true && (
            <Input
              label={t('day-of-month')}
              name="dayMonth"
              type="number"
              value={String(dayOfMonth)}
              readOnly={false}
              validations={[
                { pattern: positiveIntMinOnePattern, errorLabel: `${t('min-value')}: 1` },
                { pattern: positiveIntOneToTwentyEightPattern, errorLabel: `${t('max-value')}: 28` },
              ]}
              error={forceAssert<string>(Boolean(errorObject?.dayOfMonth))}
              required={DynamicFormRequiredType.True}
              onChange={handleInputChange('dayOfMonth')}
              className="w-10"
            />
          )}
        </div>
        <div className="mb-2 mt-3 flex flex-row space-x-4">
          <Input
            label={t('hour')}
            name="hour"
            type="number"
            value={String(hourOfDay)}
            readOnly={false}
            validations={[
              { pattern: nonNegativeIntPattern, errorLabel: `${t('min-value')}: 0` },
              { pattern: positiveIntZeroToTwentyThreePattern, errorLabel: `${t('max-value')}: 23` },
            ]}
            error={forceAssert<string>(Boolean(errorObject?.hourOfDay))}
            required={DynamicFormRequiredType.True}
            onChange={handleInputChange('hourOfDay')}
          />
          <OdinDropDown
            label={t('time-zone')}
            value={timeZone as DropDownResult}
            data={timezones}
            valueField="value"
            textField="text"
            className="ml-2 self-end"
            readOnly={false}
            error={forceAssert<string>(Boolean(errorObject?.timeZone))}
            required={DynamicFormRequiredType.True}
            onChange={(value) => handleDropDownChange('timeZone')(value as DropDownResult)}
          />
        </div>
      </div>
      {errorObject && <ErrorLabel>{t('record-validation-message')}</ErrorLabel>}
    </>
  );
};

export default IntervalFieldRule;
